/**
 * EmployeeOnboardingCard — zeigt den laufenden On-Boarding-Workflow einer
 * Mitarbeiter:in direkt in der Personalakte: Fortschrittsbalken + Aufgabenliste
 * (erledigt / offen). Versteckt sich, wenn kein aktiver On-Boarding-Workflow läuft.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { ListChecks, CheckCircle2, Circle, ArrowRight, FileText, Loader2, Upload, Check } from 'lucide-react';
import { workflowAPI } from '../../lib/api';
import api from '../../lib/api';
import { openAuthFile } from '../../lib/openAuthFile';
import { toast } from 'sonner';

const DONE = ['completed', 'skipped'];

export const EmployeeOnboardingCard = ({ employeeId, canEdit = false }) => {
  const navigate = useNavigate();
  const [wf, setWf] = useState(null);
  const [busyTaskId, setBusyTaskId] = useState(null);
  const fileInputs = useRef({});

  const load = () => {
    if (!employeeId) return;
    workflowAPI
      .getWorkflows({ employee_id: employeeId, workflow_type: 'onboarding', status: 'active' })
      .then((res) => setWf((res.data || [])[0] || null))
      .catch(() => setWf(null));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [employeeId]);

  const finishTask = async (task, documentUrl = null) => {
    setBusyTaskId(task.id);
    try {
      const res = await workflowAPI.completeTask(wf.id, task.id, { notes: null, document_url: documentUrl });
      toast.success(res.data?.workflow_completed ? 'On-Boarding vollständig abgeschlossen!' : 'Aufgabe erledigt');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aufgabe konnte nicht erledigt werden');
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleFileForTask = async (task, file) => {
    if (!file) return;
    setBusyTaskId(task.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'Sonstiges');
      fd.append('title', task.document_name || task.name);
      const up = await api.post(`/employees/${employeeId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const docId = up.data?.id;
      const url = `/api/employees/${employeeId}/documents/${docId}/download`;
      await finishTask(task, url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
      setBusyTaskId(null);
    }
  };

  if (!wf) return null;

  const tasks = wf.tasks || [];
  const doneCount = tasks.filter((t) => DONE.includes(t.status)).length;

  return (
    <Card className="border-primary/30 bg-primary/[0.03]" data-testid="employee-onboarding-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-heading flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-primary" />
            On-Boarding läuft
          </CardTitle>
          <Badge variant="secondary" data-testid="onboarding-progress-badge">
            {doneCount}/{tasks.length} erledigt
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress value={wf.progress || 0} className="h-2" />
        <div className="space-y-2">
          {tasks.map((t, i) => {
            const done = DONE.includes(t.status);
            return (
              <div
                key={t.id || i}
                className="flex items-start gap-3 py-1"
                data-testid={`onboarding-task-${i}`}
              >
                {done
                  ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                  : <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${done ? 'text-muted-foreground line-through' : 'font-medium'}`}>
                    {t.name}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    {t.assigned_role && <span>{t.assigned_role}</span>}
                    {done && t.completed_by_name && <span>· erledigt von {t.completed_by_name}</span>}
                    {t.document_url && (
                      <button
                        type="button"
                        onClick={() =>
                          openAuthFile(t.document_url, { filename: `${t.document_name || t.name}.pdf` })
                            .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
                        }
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        data-testid={`onboarding-task-doc-${i}`}
                      >
                        <FileText className="h-3 w-3" /> Dokument
                      </button>
                    )}
                  </div>
                </div>
                {!done && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-300">offen</Badge>
                    {canEdit && (
                      t.requires_document ? (
                        <>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.docx"
                            className="hidden"
                            ref={(el) => { fileInputs.current[t.id] = el; }}
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFileForTask(t, f); }}
                            data-testid={`onboarding-task-file-${i}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1"
                            disabled={busyTaskId === t.id}
                            onClick={() => fileInputs.current[t.id]?.click()}
                            data-testid={`onboarding-task-upload-${i}`}
                          >
                            {busyTaskId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                            Dokument &amp; erledigen
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1"
                          disabled={busyTaskId === t.id}
                          onClick={() => finishTask(t)}
                          data-testid={`onboarding-task-done-${i}`}
                        >
                          {busyTaskId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Erledigen
                        </Button>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-primary"
          onClick={() => navigate(`/workflows/${wf.id}`)}
          data-testid="onboarding-open-workflow-btn"
        >
          Workflow öffnen <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default EmployeeOnboardingCard;
